import React from 'react';

// Normaliza texto (remove acentos, lowercase)
export const normalizeText = (text) => {
    if (!text) return '';
    return text
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
};

// Normaliza e valida a query de busca
const normalizeAndValidateQuery = (searchQuery) => {
    if (!searchQuery || !searchQuery.trim()) return null;
    return normalizeText(searchQuery) || null;
};

// Extrai texto recursivamente de um elemento React (para headers e labels que já são strings/elementos simples)
const extractTextFromElement = (element) => {
    if (!element) return '';
    if (typeof element === 'string') return element;
    if (typeof element === 'number') return element.toString();
    if (Array.isArray(element)) return element.map(extractTextFromElement).join(' ');
    if (React.isValidElement(element) && element.props?.children) {
        return extractTextFromElement(element.props.children);
    }
    return '';
};

// Extrai texto traduzido do label
// Campos customizados: tratados separadamente em matchesCustomFields
const extractLabelText = (label, intl) => {
    if (!label) return '';
    if (typeof label === 'string') return label;

    if (React.isValidElement(label)) {
        // FormattedMessage tem props.id, traduz com intl
        if (label.props?.id && intl) {
            return intl.formatMessage({ id: label.props.id });
        }
        // Elemento React com children 
        if (label.props?.children) {
            return extractTextFromElement(label);
        }
    }

    return '';
};

// Verifica match em campos customizados (usam nome do campo diretamente, não precisam de intl)
const matchesCustomFields = (customFields, normalizedQuery) => {
    if (!Array.isArray(customFields) || !normalizedQuery) return false;
    return customFields.some((item) => {
        const fieldLabel = item.descricaoCampo || item.campo?.nome || '';
        return fieldLabel && normalizeText(fieldLabel).includes(normalizedQuery);
    });
};

// Verifica se é um container HTML ou Row/Col do reactstrap
const isContainerElement = (type) => {
    if (typeof type === 'string') return true;
    if (!type) return false;
    return (
        type.name === 'Row' ||
        type.name === 'Col' ||
        type.displayName === 'Row' ||
        type.displayName === 'Col'
    );
};

// Verifica se é DesenharCampoCustomizado pelo formato do model.value
const isDesenharCampoCustomizado = (props) => {
    return (
        props?.model?.value &&
        Array.isArray(props.model.value) &&
        props.model.value.length > 0 &&
        props.model.value[0]?.campo
    );
};

// Verifica se o resultado filtrado está vazio
const isEmptyFilteredResult = (filteredChildren) => {
    if (!filteredChildren) return true;
    if (!Array.isArray(filteredChildren)) return false;
    if (filteredChildren.length === 0) return true;
    return filteredChildren.every((c) => c === null || c === false || c === undefined);
};

// Verifica se um elemento corresponde à busca
const checkElementMatch = (element, normalizedQuery, intl) => {
    if (!element || !normalizedQuery) return false;

    if (typeof element === 'string' || typeof element === 'number') {
        return normalizeText(element.toString()).includes(normalizedQuery);
    }

    if (React.isValidElement(element) && element.props?.model) {
        const { model } = element.props;

        // Verifica label traduzido
        const labelText = extractLabelText(model.label, intl);
        if (labelText && normalizeText(labelText).includes(normalizedQuery)) {
            return true;
        }

        // Verifica campos customizados (nome do campo direto)
        if (matchesCustomFields(model.value, normalizedQuery)) {
            return true;
        }
    }

    return false;
};

// Verifica se algum filho tem match com a busca (usado para decidir se mostra/esconde o collapse)
export const findLabelsInChildren = (children, searchQuery, intl) => {
    if (!searchQuery || !children) return false;

    const normalizedQuery = normalizeAndValidateQuery(searchQuery);
    if (!normalizedQuery) return false;

    const traverse = (element) => {
        if (!element) return false;
        if (Array.isArray(element)) return element.some(traverse);
        if (checkElementMatch(element, normalizedQuery, intl)) return true;
        if (React.isValidElement(element) && element.props?.children) {
            return traverse(element.props.children);
        }
        return false;
    };

    return traverse(children);
};

// Verifica se um elemento individual corresponde à busca 
export const elementMatchesSearch = (element, searchQuery, intl) => {
    if (!searchQuery || !searchQuery.trim() || !element) return true;

    const normalizedQuery = normalizeAndValidateQuery(searchQuery);
    if (!normalizedQuery) return true;

    if (Array.isArray(element)) {
        return element.some((item) => elementMatchesSearch(item, searchQuery, intl));
    }

    if (checkElementMatch(element, normalizedQuery, intl)) return true;

    if (React.isValidElement(element) && element.props?.children) {
        return elementMatchesSearch(element.props.children, searchQuery, intl);
    }

    return false;
};

// Filtra children recursivamente, removendo os que não correspondem à busca
export const filterChildren = (children, searchQuery, intl) => {
    if (!searchQuery || !searchQuery.trim() || !children) return children;

    const normalizedQuery = normalizeAndValidateQuery(searchQuery);
    if (!normalizedQuery) return children;

    if (Array.isArray(children)) {
        const filtered = children
            .map((child) => filterChildren(child, searchQuery, intl))
            .filter((child) => child !== null && child !== false && child !== undefined);
        return filtered.length > 0 ? filtered : null;
    }

    if (React.isValidElement(children)) {
        const { type, props } = children;

        if (isContainerElement(type) && props?.children) {
            const filteredChildren = filterChildren(props.children, searchQuery, intl);
            if (isEmptyFilteredResult(filteredChildren)) return null;
            return React.cloneElement(children, { ...props, children: filteredChildren });
        }

        // DesenharCampoCustomizado, filtra pelos nomes dos campos
        if (isDesenharCampoCustomizado(props)) {
            const visibleFields = props.model.value
                .filter((item) => {
                    const fieldLabel = item.descricaoCampo || item.campo?.nome || '';
                    return fieldLabel && normalizeText(fieldLabel).includes(normalizedQuery);
                })
                .map((item) => ({ id: item.idCampo || item.campo?.id }));

            if (visibleFields.length === 0) return null;
            return React.cloneElement(children, { ...props, visibleFields, useVisibleFields: true });
        }

        if (!elementMatchesSearch(children, searchQuery, intl)) return null;

        // Se tem children, filtra recursivamente
        if (props?.children) {
            const filteredChildren = filterChildren(props.children, searchQuery, intl);
            if (isEmptyFilteredResult(filteredChildren)) return null;
            return React.cloneElement(children, { ...props, children: filteredChildren });
        }

        return children;
    }

    return children;
};

// Extrai texto do header 
export const extractHeaderText = (header) => {
    if (!header) return '';
    if (typeof header === 'string') return header;
    if (React.isValidElement(header)) return extractTextFromElement(header);
    return '';
};
